// src/components/ui/EntityTableView.jsx
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Tooltip,
  useTheme,
  alpha,
  useMediaQuery
} from '@mui/material';
import { 
  MoreVert as MoreIcon,
  ViewList as ViewIcon
} from '@mui/icons-material';

/**
 * A reusable table view component for displaying entities in a table layout
 * 
 * @param {Object} props
 * @param {Array} props.items - Array of entity items
 * @param {Function} props.renderTableRow - Function to render each table row (optional)
 * @param {Array} props.columns - Column configuration
 * @param {Function} props.onRowClick - Function called when a row is clicked
 * @param {Function} props.keyExtractor - Function to extract unique key for each item
 * @param {Function} props.onActionClick - Function called when the action button is clicked
 * @param {Function} props.renderRowActions - Function to render row actions (optional)
 */
const EntityTableView = ({
  items = [],
  renderTableRow,
  columns = [],
  onRowClick,
  keyExtractor = (item) => item.id || item._id,
  onActionClick,
  renderRowActions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('sm') || '(max-width: 600px)');

  // Adjust columns based on screen size
  const visibleColumns = React.useMemo(() => {
    if (isMobile) {
      // On mobile, only show first column
      return columns.slice(0, 1);
    }
    return columns;
  }, [columns, isMobile]);

  // If a custom row renderer is provided, use it
  if (renderTableRow) {
    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          width: '100%', 
          overflowX: 'auto',
          boxShadow: 'none',
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Table 
          aria-label="entities table" 
          size={isMobile ? "small" : "medium"} 
          stickyHeader
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell 
                  key={column.id} 
                  align={column.align || 'left'}
                  sx={{ 
                    fontWeight: 'bold',
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell 
                align="right" 
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  width: '110px'
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => renderTableRow(item, index))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Default rendering based on columns configuration
  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        width: '100%', 
        overflowX: 'auto',
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <Table 
        aria-label="entities table" 
        size={isMobile ? "small" : "medium"}
        stickyHeader
      >
        <TableHead>
          <TableRow>
            {visibleColumns.map((column) => (
              <TableCell 
                key={column.id} 
                align={column.align || 'left'}
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }}
              >
                {column.label}
              </TableCell>
            ))}
            <TableCell 
              align="right" 
              sx={{ 
                fontWeight: 'bold',
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                width: '110px'
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow
              key={keyExtractor(item)}
              hover
              onClick={onRowClick ? () => onRowClick(item) : undefined}
              sx={{ 
                cursor: onRowClick ? 'pointer' : 'default',
                '&:nth-of-type(even)': {
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                },
              }}
            >
              {visibleColumns.map((column) => (
                <TableCell 
                  key={`${keyExtractor(item)}-${column.id}`} 
                  align={column.align || 'left'}
                >
                  {column.renderCell ? column.renderCell(item) : item[column.id]}
                </TableCell>
              ))}
              <TableCell align="right">
                {renderRowActions ? (
                  renderRowActions(item, index)
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onRowClick) onRowClick(item);
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {onActionClick && (
                      <Tooltip title="More Actions">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            onActionClick(e, item);
                          }}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EntityTableView;